import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Music, 
  Upload, 
  Trash2, 
  Edit3, 
  Plus,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader2,
  Check,
  X,
  Search,
  FileText,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { useDesign } from '@/contexts/DesignContext';
import { Slotted } from '@/components/studio/admin/chromeSlots';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function SongsByThemeManager() {
  const { isStudio } = useDesign();
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedThemes, setExpandedThemes] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  
  // Import dialog state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importContent, setImportContent] = useState('');
  const [importing, setImporting] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  
  // Edit theme dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);
  const [editFormData, setEditFormData] = useState({ theme: '', songs: [] });
  const [saving, setSaving] = useState(false);
  
  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [themeToDelete, setThemeToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Add theme dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newThemeData, setNewThemeData] = useState({ theme: '', songs: [] });

  // Fetch all themes
  const fetchThemes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/songs-by-theme/`);
      const data = await response.json();
      setThemes(data.themes || []);
    } catch (err) {
      console.error('Error fetching themes:', err);
      toast.error('Failed to fetch themes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThemes();
  }, []);

  // Toggle theme expansion
  const toggleTheme = (themeName) => {
    const newExpanded = new Set(expandedThemes);
    if (newExpanded.has(themeName)) {
      newExpanded.delete(themeName);
    } else {
      newExpanded.add(themeName);
    }
    setExpandedThemes(newExpanded);
  };

  // Preview import
  const handlePreviewImport = async () => {
    if (!importContent.trim()) {
      toast.error('Please paste your song list first');
      return;
    }

    setImporting(true);
    try {
      const response = await fetch(`${API_URL}/api/songs-by-theme/preview-import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: importContent })
      });
      
      const data = await response.json();
      
      if (data.success && data.themes?.length > 0) {
        setPreviewData(data);
        setShowPreview(true);
      } else {
        toast.error('No themes could be parsed from the content. Make sure your format is correct.');
      }
    } catch (err) {
      console.error('Error previewing import:', err);
      toast.error('Failed to preview import');
    } finally {
      setImporting(false);
    }
  };

  // Confirm import
  const handleConfirmImport = async () => {
    setImporting(true);
    try {
      const response = await fetch(`${API_URL}/api/songs-by-theme/bulk-import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: importContent })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message);
        setShowImportDialog(false);
        setShowPreview(false);
        setImportContent('');
        setPreviewData(null);
        fetchThemes();
      } else {
        toast.error(data.detail || 'Import failed');
      }
    } catch (err) {
      console.error('Error importing:', err);
      toast.error('Failed to import');
    } finally {
      setImporting(false);
    }
  };

  // Edit theme
  const handleEditTheme = (theme) => {
    setEditingTheme(theme);
    setEditFormData({
      theme: theme.theme,
      songs: theme.songs || []
    });
    setShowEditDialog(true);
  };

  // Save edited theme
  const handleSaveTheme = async () => {
    if (!editFormData.theme.trim()) {
      toast.error('Theme name is required');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/songs-by-theme/theme/${encodeURIComponent(editingTheme.theme)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          theme: editFormData.theme,
          songs: editFormData.songs
        })
      });
      
      if (response.ok) {
        toast.success('Theme updated successfully');
        setShowEditDialog(false);
        setEditingTheme(null);
        fetchThemes();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update theme');
      }
    } catch (err) {
      console.error('Error updating theme:', err);
      toast.error('Failed to update theme');
    } finally {
      setSaving(false);
    }
  };

  // Delete theme
  const handleDeleteTheme = (theme) => {
    setThemeToDelete(theme);
    setShowDeleteDialog(true);
  };

  const confirmDeleteTheme = async () => {
    if (!themeToDelete) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/songs-by-theme/theme/${encodeURIComponent(themeToDelete.theme)}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Theme deleted');
        fetchThemes();
      } else {
        toast.error('Failed to delete theme');
      }
    } catch (err) {
      console.error('Error deleting theme:', err);
      toast.error('Failed to delete theme');
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
      setThemeToDelete(null);
    }
  };

  // Add new theme
  const handleAddTheme = async () => {
    if (!newThemeData.theme.trim()) {
      toast.error('Theme name is required');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/songs-by-theme/theme`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newThemeData)
      });
      
      if (response.ok) {
        toast.success('Theme created successfully');
        setShowAddDialog(false);
        setNewThemeData({ theme: '', songs: [] });
        fetchThemes();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to create theme');
      }
    } catch (err) {
      console.error('Error creating theme:', err);
      toast.error('Failed to create theme');
    } finally {
      setSaving(false);
    }
  };

  // Add song to edit form
  const addSongToForm = (formSetter, currentSongs) => {
    formSetter(prev => ({
      ...prev,
      songs: [...currentSongs, { title: '', artist: '', youtube_url: '' }]
    }));
  };

  // Update song in form
  const updateSongInForm = (formSetter, currentSongs, index, field, value) => {
    const newSongs = [...currentSongs];
    newSongs[index] = { ...newSongs[index], [field]: value };
    formSetter(prev => ({ ...prev, songs: newSongs }));
  };

  // Remove song from form
  const removeSongFromForm = (formSetter, currentSongs, index) => {
    const newSongs = currentSongs.filter((_, i) => i !== index);
    formSetter(prev => ({ ...prev, songs: newSongs }));
  };

  // Filter themes by search
  const filteredThemes = themes.filter(theme => 
    theme.theme.toLowerCase().includes(searchQuery.toLowerCase()) ||
    theme.songs?.some(song => 
      song.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      song.artist?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  // Calculate stats
  const totalSongs = themes.reduce((acc, theme) => acc + (theme.songs?.length || 0), 0);

  return (
    <div className={isStudio ? 'st31-stack' : 'space-y-4'}>
      {isStudio && (<>
        {/* The counts, the search and the two buttons live in the shared header
           (mock frames 19, 20) — portalled so none of this state moves. */}
        <Slotted name="sub">{themes.length} themes · {totalSongs} songs</Slotted>
        <Slotted name="search">
          <Search />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search themes or songs..."
            data-testid="theme-search-input"
          />
        </Slotted>
        <Slotted name="right">
          <button type="button" className="st-btn st-ghost st-sm" onClick={() => setShowAddDialog(true)} data-testid="add-theme-btn">
            <Plus /> Add theme
          </button>
          <button type="button" className="st-btn st-pri st-sm" onClick={() => setShowImportDialog(true)} data-testid="bulk-import-btn">
            <Upload /> Bulk import
          </button>
        </Slotted>
      </>)}
      {/* Header with stats and actions */}
      {!isStudio && (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-md shadow-emerald-500/25">
            <Music className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-semibold text-slate-800 dark:text-slate-100">Songs By Theme</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {themes.length} themes, {totalSongs} songs
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddDialog(true)}
            className="gap-2"
            data-testid="add-theme-btn"
          >
            <Plus className="w-4 h-4" />
            Add Theme
          </Button>
          <Button
            size="sm"
            onClick={() => setShowImportDialog(true)}
            className="gap-2 bg-emerald-600 hover:bg-emerald-700"
            data-testid="bulk-import-btn"
          >
            <Upload className="w-4 h-4" />
            Bulk Import
          </Button>
        </div>
      </div>
      )}

      {/* Search */}
      {!isStudio && (
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
        <Input
          type="text"
          placeholder="Search themes or songs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-card"
          data-testid="theme-search-input"
        />
      </div>
      )}

      {/* Themes List */}
      <div className={isStudio ? 'st31-stack' : 'space-y-2'}>
        {loading ? (isStudio ? (
          <div className="st31-card"><div className="st31-cb">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="st31-skrow" style={{ height: 44 }}>
                <span className="st31-sk" style={{ width: 16, height: 16, borderRadius: 4 }} />
                <span className="st31-skcol">
                  <span className="st31-sk" style={{ width: '32%', height: 14 }} />
                  <span className="st31-sk" style={{ width: '18%', height: 12 }} />
                </span>
              </div>
            ))}
          </div></div>
        ) : (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-emerald-500" />
            <p className="text-slate-500 dark:text-slate-400 mt-2">Loading themes...</p>
          </div>
        )) : filteredThemes.length === 0 ? (isStudio ? (
          <div className="st31-card">
            <div className="st-empty" style={{ padding: '40px 24px' }}>
              <span className="st-glyph">{themes.length === 0 ? <Music /> : <Search />}</span>
              <b>{themes.length === 0 ? 'No themes yet' : `No theme or song matches “${searchQuery}”`}</b>
              <span>{themes.length === 0 ? 'Import your song list and the AI can suggest from it.' : 'Try another word, or clear the search.'}</span>
              {themes.length === 0 ? (
                <button type="button" className="st-btn st-sec st-sm" style={{ marginTop: 10 }} onClick={() => setShowImportDialog(true)}>
                  <Upload /> Import songs
                </button>
              ) : (
                <button type="button" className="st-btn st-sec st-sm" style={{ marginTop: 10 }} onClick={() => setSearchQuery('')}>Clear search</button>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-12 bg-card rounded-xl border border-slate-200 dark:border-white/10">
            <Music className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 dark:text-slate-400 mb-4">
              {themes.length === 0 
                ? "No themes yet. Import your song list to get started!"
                : "No themes match your search"}
            </p>
            {themes.length === 0 && (
              <Button
                onClick={() => setShowImportDialog(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import Songs
              </Button>
            )}
          </div>
        )) : (
          filteredThemes.map((theme, index) => (
            <div
              key={theme.theme || index}
              className={isStudio ? 'st31-card' : 'bg-card rounded-xl border border-slate-200 dark:border-white/10 overflow-hidden'}
              data-testid={`theme-card-${index}`}
            >
              {/* Theme Header */}
              <div 
                className={isStudio ? 'st31-thm' : 'flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5 transition-colors'}
                onClick={() => toggleTheme(theme.theme)}
              >
                {isStudio ? (<>
                  {expandedThemes.has(theme.theme) ? <ChevronDown style={{ width: 16, height: 16, color: 'var(--st-fg3)' }} /> : <ChevronRight style={{ width: 16, height: 16, color: 'var(--st-fg3)' }} />}
                  <span className="st31-c">
                    <span className="st31-nm">{theme.theme}</span>
                    <span className="st31-mt">{theme.songs?.length || 0} songs</span>
                  </span>
                  <span style={{ display: 'flex', gap: 2 }} onClick={(e) => e.stopPropagation()}>
                    <button type="button" className="st-ibtn" style={{ width: 28, height: 28 }} onClick={() => handleEditTheme(theme)} data-testid={`edit-theme-${index}`} aria-label="Edit theme"><Edit3 /></button>
                    <button type="button" className="st-ibtn" style={{ width: 28, height: 28 }} onClick={() => handleDeleteTheme(theme)} data-testid={`delete-theme-${index}`} aria-label="Delete theme"><Trash2 /></button>
                  </span>
                </>) : (<>
                <div className="flex items-center gap-3">
                  {expandedThemes.has(theme.theme) ? (
                    <ChevronDown className="w-5 h-5 text-slate-400 dark:text-slate-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-400 dark:text-slate-500" />
                  )}
                  <div>
                    <h3 className="font-semibold text-slate-800 dark:text-slate-100">{theme.theme}</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{theme.songs?.length || 0} songs</p>
                  </div>
                </div>
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEditTheme(theme)}
                    className="h-8 w-8 text-slate-400 dark:text-slate-500 hover:text-blue-600"
                    data-testid={`edit-theme-${index}`}
                  >
                    <Edit3 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteTheme(theme)}
                    className="h-8 w-8 text-slate-400 dark:text-slate-500 hover:text-red-600"
                    data-testid={`delete-theme-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                </>)}
              </div>
              
              {/* Expanded Songs List */}
              {expandedThemes.has(theme.theme) && theme.songs?.length > 0 && (
                <div className={isStudio ? 'st31-songbody' : 'border-t border-slate-100 dark:border-white/10 bg-slate-50 dark:bg-[#1e1f22] p-4'}>
                  <div className={isStudio ? undefined : 'space-y-2'} style={isStudio ? { display: 'flex', flexDirection: 'column', gap: 8 } : undefined}>
                    {theme.songs.map((song, songIndex) => (
                      <div 
                        key={songIndex}
                        className={isStudio ? 'st31-song' : 'flex items-center justify-between p-2 bg-card rounded-lg border border-slate-100 dark:border-white/10'}
                      >
                        <div className={isStudio ? undefined : 'flex items-center gap-2 min-w-0 flex-1'} style={isStudio ? { display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 } : undefined}>
                          <Music className={isStudio ? undefined : 'w-4 h-4 text-emerald-500 flex-shrink-0'} style={isStudio ? { width: 15, height: 15, color: 'var(--st-fg3)', flex: 'none' } : undefined} />
                          <span className={isStudio ? undefined : 'font-medium text-slate-700 dark:text-slate-200 truncate'} style={isStudio ? { fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : undefined}>{song.title}</span>
                          {song.artist && (
                            <span className={isStudio ? 'st31-a' : 'text-slate-400 dark:text-slate-500 text-sm truncate'}>{isStudio ? song.artist : `- ${song.artist}`}</span>
                          )}
                        </div>
                        {song.youtube_url ? (
                          <a
                            href={song.youtube_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={isStudio ? undefined : 'text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-200 flex-shrink-0 ml-2'}
                            style={isStudio ? { color: 'var(--st-fg2)', marginLeft: 'auto', display: 'inline-flex', flex: 'none' } : undefined}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className={isStudio ? undefined : 'w-4 h-4'} style={isStudio ? { width: 15, height: 15 } : undefined} />
                          </a>
                        ) : (isStudio ? <span className="st31-a" style={{ marginLeft: 'auto' }}>No link</span> : null)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Bulk Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-emerald-500" />
              Bulk Import Songs By Theme
            </DialogTitle>
          </DialogHeader>
          
          {!showPreview ? (
            <div className="space-y-4 pt-4">
              <div className="bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900 rounded-lg p-4">
                <h4 className="font-medium text-emerald-800 dark:text-emerald-200 mb-2">Expected Format:</h4>
                <pre className="text-sm text-emerald-700 dark:text-emerald-300 whitespace-pre-wrap font-mono">
{`Obedience
Build My Life https://youtu.be/Z32HiCoFzlU
Yes I Will https://youtu.be/U-fu6lfrA-0

Grace
Amazing Grace https://youtu.be/Zev5tHjCB_s
Your Grace Is Enough https://youtu.be/abc123`}
                </pre>
                <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-2">
                  Theme headers on their own line, songs with YouTube URLs below.
                  No dashes needed!
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Paste Your Song List</Label>
                <Textarea
                  value={importContent}
                  onChange={(e) => setImportContent(e.target.value)}
                  placeholder="Paste your categorized song list here..."
                  rows={15}
                  className="font-mono text-sm"
                  data-testid="import-content-textarea"
                />
              </div>
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowImportDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handlePreviewImport} 
                  disabled={importing || !importContent.trim()}
                  className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="preview-import-btn"
                >
                  {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                  Preview Import
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 pt-4">
              <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-1">Preview Summary</h4>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Found <strong>{previewData?.total_themes || 0}</strong> themes with{' '}
                  <strong>{previewData?.total_songs || 0}</strong> total songs
                </p>
              </div>
              
              <ScrollArea className="h-[400px] border rounded-lg">
                <div className="p-4 space-y-4">
                  {previewData?.themes?.map((theme, index) => (
                    <div key={index} className="border-b border-slate-100 dark:border-white/10 pb-4 last:border-0">
                      <h4 className="font-semibold text-slate-800 dark:text-slate-100 mb-2">
                        {theme.theme} ({theme.song_count} songs)
                      </h4>
                      <div className="space-y-1 pl-4">
                        {theme.songs?.slice(0, 5).map((song, songIndex) => (
                          <div key={songIndex} className="text-sm text-slate-600 dark:text-slate-300 flex items-center gap-2">
                            <Music className="w-3 h-3 text-emerald-500" />
                            <span>{song.title}</span>
                            {song.artist && <span className="text-slate-400 dark:text-slate-500">- {song.artist}</span>}
                            {song.youtube_url && (
                              <span className="text-emerald-600 dark:text-emerald-400 text-xs">(has link)</span>
                            )}
                          </div>
                        ))}
                        {theme.songs?.length > 5 && (
                          <p className="text-xs text-slate-400 dark:text-slate-500 italic">
                            + {theme.songs.length - 5} more songs
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              
              <div className="flex justify-between">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowPreview(false);
                    setPreviewData(null);
                  }}
                >
                  Back to Edit
                </Button>
                <Button 
                  onClick={handleConfirmImport} 
                  disabled={importing}
                  className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="confirm-import-btn"
                >
                  {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Confirm Import
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Theme Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-blue-500 dark:text-blue-400" />
              Edit Theme
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Theme Name</Label>
              <Input
                value={editFormData.theme}
                onChange={(e) => setEditFormData({ ...editFormData, theme: e.target.value })}
                placeholder="e.g., Grace, Cross, Hope"
                data-testid="edit-theme-name"
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Songs ({editFormData.songs.length})</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addSongToForm(setEditFormData, editFormData.songs)}
                  className="gap-1"
                >
                  <Plus className="w-3 h-3" /> Add Song
                </Button>
              </div>
              
              <ScrollArea className="h-[300px] border rounded-lg p-3">
                <div className="space-y-3">
                  {editFormData.songs.map((song, index) => (
                    <div key={index} className="grid grid-cols-[1fr,1fr,auto] gap-2 items-start p-2 bg-slate-50 dark:bg-[#1e1f22] rounded-lg">
                      <Input
                        value={song.title || ''}
                        onChange={(e) => updateSongInForm(setEditFormData, editFormData.songs, index, 'title', e.target.value)}
                        placeholder="Song title"
                        className="text-sm"
                      />
                      <Input
                        value={song.youtube_url || ''}
                        onChange={(e) => updateSongInForm(setEditFormData, editFormData.songs, index, 'youtube_url', e.target.value)}
                        placeholder="YouTube URL (optional)"
                        className="text-sm"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeSongFromForm(setEditFormData, editFormData.songs, index)}
                        className="h-9 w-9 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-200 hover:bg-red-50 dark:hover:bg-red-500/15"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {editFormData.songs.length === 0 && (
                    <p className="text-center text-slate-400 dark:text-slate-500 py-8">
                      No songs yet. Click "Add Song" to start.
                    </p>
                  )}
                </div>
              </ScrollArea>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveTheme} 
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Theme Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-emerald-500" />
              Add New Theme
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Theme Name *</Label>
              <Input
                value={newThemeData.theme}
                onChange={(e) => setNewThemeData({ ...newThemeData, theme: e.target.value })}
                placeholder="e.g., Grace, Cross, Hope"
                data-testid="new-theme-name"
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Songs ({newThemeData.songs.length})</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addSongToForm(setNewThemeData, newThemeData.songs)}
                  className="gap-1"
                >
                  <Plus className="w-3 h-3" /> Add Song
                </Button>
              </div>
              
              <ScrollArea className="h-[250px] border rounded-lg p-3">
                <div className="space-y-3">
                  {newThemeData.songs.map((song, index) => (
                    <div key={index} className="grid grid-cols-[1fr,1fr,auto] gap-2 items-start p-2 bg-slate-50 dark:bg-[#1e1f22] rounded-lg">
                      <Input
                        value={song.title || ''}
                        onChange={(e) => updateSongInForm(setNewThemeData, newThemeData.songs, index, 'title', e.target.value)}
                        placeholder="Song title"
                        className="text-sm"
                      />
                      <Input
                        value={song.youtube_url || ''}
                        onChange={(e) => updateSongInForm(setNewThemeData, newThemeData.songs, index, 'youtube_url', e.target.value)}
                        placeholder="YouTube URL (optional)"
                        className="text-sm"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeSongFromForm(setNewThemeData, newThemeData.songs, index)}
                        className="h-9 w-9 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-200 hover:bg-red-50 dark:hover:bg-red-500/15"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {newThemeData.songs.length === 0 && (
                    <p className="text-center text-slate-400 dark:text-slate-500 py-8">
                      No songs yet. Click "Add Song" to start adding songs.
                    </p>
                  )}
                </div>
              </ScrollArea>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddTheme} 
                disabled={saving || !newThemeData.theme.trim()}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Create Theme
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500 dark:text-red-400" />
              Delete Theme
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the theme &quot;{themeToDelete?.theme}&quot; 
              and all {themeToDelete?.songs?.length || 0} songs? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteTheme}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default SongsByThemeManager;
